import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Network, AlertTriangle, User, Users, HardHat, Crown, ChevronRight, Save } from 'lucide-react';
import { toast } from 'sonner';

const LEVEL_ICONS = { 1: User, 2: Users, 3: HardHat, 4: Crown };
const LEVEL_COLORS = {
  1: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  2: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  3: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  4: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
};
const SEV = { critic: 'bg-rose-500 text-white', urgent: 'bg-orange-500 text-white', atentie: 'bg-amber-500 text-black', normal: 'bg-zinc-600 text-white' };

export default function EscalationPage() {
  const [levels, setLevels] = useState([]);
  const [byLevel, setByLevel] = useState({});
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    API.get('/escalation/alerts').then(r => { setLevels(r.data.levels); setByLevel(r.data.by_level); setAlerts(r.data.alerts); }).catch(() => {});
  }, []);

  const updateLevel = (levelNum, field, val) => setLevels(ls => ls.map(l => l.level === levelNum ? { ...l, [field]: val } : l));
  const saveLevels = async () => {
    try { await API.put('/escalation/config', { levels }); toast.success('Persoane responsabile salvate'); }
    catch { toast.error('Eroare salvare'); }
  };

  return (
    <div className="p-8" data-testid="escalation-page">
      <div className="flex items-center gap-3 mb-2">
        <Network className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Matrice de Escaladare</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Escaladare multi-nivel: Ofertant → Manager Licitatii → Ingineri → Director, in functie de termene si riscuri.</p>

      {/* Matrix definition with editable responsible persons */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-muted-foreground" style={{ fontFamily: 'Outfit' }}>Persoane responsabile pe nivel</p>
        <Button size="sm" onClick={saveLevels} className="text-xs h-8" data-testid="save-escalation-btn"><Save className="w-3.5 h-3.5 mr-1.5" />Salveaza</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-8">
        {levels.map((lv) => {
          const Icon = LEVEL_ICONS[lv.level] || User;
          const count = (byLevel[lv.level] || []).length;
          return (
            <Card key={lv.level} className={`border ${LEVEL_COLORS[lv.level]}`} data-testid={`escalation-level-${lv.level}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4" />
                  <span className="text-[10px] font-mono-data opacity-70">Nivel {lv.level}</span>
                  {count > 0 && <Badge className="ml-auto bg-white/10 text-[9px]">{count}</Badge>}
                </div>
                <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>{lv.role}</p>
                <p className="text-[10px] opacity-70 mt-1 mb-2 leading-tight">{lv.trigger}</p>
                <Input value={lv.name || ''} onChange={e => updateLevel(lv.level, 'name', e.target.value)}
                  placeholder="Nume persoana" className="h-8 text-xs bg-black/20 border-white/10 mb-1.5" data-testid={`escalation-name-${lv.level}`} />
                <Input value={lv.email || ''} onChange={e => updateLevel(lv.level, 'email', e.target.value)}
                  placeholder="email@saga.ro" className="h-8 text-xs bg-black/20 border-white/10" data-testid={`escalation-email-${lv.level}`} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Live alerts grouped by level */}
      <div className="space-y-6">
        {[4, 3, 2, 1].map(lvNum => {
          const items = byLevel[lvNum] || [];
          if (!items.length) return null;
          const lv = levels.find(l => l.level === lvNum) || {};
          const Icon = LEVEL_ICONS[lvNum] || User;
          return (
            <Card key={lvNum} className="bg-card border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Icon className="w-4 h-4" /> Nivel {lvNum}: {lv.role} <Badge className="bg-white/5 text-[9px]">{items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {items.map((a, i) => (
                  <Link key={i} to={a.project_id ? `/projects/${a.project_id}` : '#'}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900/50 border border-white/5 hover:bg-white/5 transition-colors" data-testid={`escalation-alert-${lvNum}-${i}`}>
                    <Badge className={`text-[9px] ${SEV[a.severity]}`}>{a.days_left < 0 ? `${Math.abs(a.days_left)}z depasit` : `${a.days_left}z`}</Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{a.project_name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{a.title}</p>
                    </div>
                    {a.win_score > 0 && <span className="text-[10px] text-emerald-400 font-mono-data">{a.win_score}%</span>}
                    <span className="text-[10px] text-muted-foreground font-mono-data">{a.date}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  </Link>
                ))}
              </CardContent>
            </Card>
          );
        })}
        {!alerts.length && (
          <div className="text-center py-12 text-muted-foreground">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nicio escaladare activa. Adauga termene la licitatii pentru a activa matricea.</p>
          </div>
        )}
      </div>
    </div>
  );
}
